/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, alpha, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import SearchInput from '../../components/inputs/searchInput';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import debounce from 'lodash.debounce';
import Popup from '../../components/popups/popup';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import BranchDropdown from '../../components/dropdowns/branchDropdown';
import { Link } from 'react-router-dom';
import StatusDropdown from '../../components/dropdowns/statusDropdown';
import PageService from '../../services/pageService';
import ViewPageModal from '../../components/modals/page/viewPageModal';
import DeletePageModal from '../../components/modals/page/deletePageModal';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from '../../components/inputs/onOffSwitchForDeleted';
import { formatPrice } from '../../utils';
import PdfPageList from '../../components/prints/pdfPageList';

const Pages = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [pages, setPages] = useState([]);
    const [selectedPage, setSelectedPage] = useState({});
    const [totalWallet, setTotalWallet] = useState(0);
    const [totalRemainingAmount, setTotalRemainingAmount] = useState(0);

    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (page) => {
        setSelectedPage(page);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);


    const handleOpenDelete = (page) => {
        setSelectedPage(page);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSearchTerm('');
            setSelectedBranch(null);
            setSelectedStatus('');
            await fetchPages();
        }
    };

    const handleStatus = (status) => {
        if (status === t("active")) return "active";
        else if (status === t("inactive")) return "inactive";
    };

    const fetchPages = async (page = 1, name = '', branchId = null, status = '') => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                name, branchId,
                status: handleStatus(status),
                deleted,
                ...sortState
            };
            const data = await PageService.fetch(page, filters);
            const { pages, totalWallet, totalRemainingAmount } = data;
            setTotalWallet(totalWallet);
            setTotalRemainingAmount(totalRemainingAmount);

            if (pages.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setPages([]);
                }
            } else {
                setPages((prevs) => (page === 1 ? pages : [...prevs, ...pages]));
            }
        } catch (error) {
            console.error('Error fetching pages:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedFetchPages = useMemo(() =>
        debounce((name, branchId, status) => {
            setPage(1);
            setHasMore(true);
            fetchPages(1, name, branchId, status);
        }, 1000),
        []
    );

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        debouncedFetchPages(value, selectedBranch?.id, selectedStatus);
    };

    const resetFilter = () => {
        setSearchTerm('');
        setHasMore(true)
        setSelectedBranch(null);
        setSelectedStatus('');
        if (page !== 1) setPage(1);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchPages(page, searchTerm, selectedBranch?.id, selectedStatus);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchPages(1, searchTerm, selectedBranch?.id, selectedStatus);
    }, [selectedBranch, selectedStatus, sortState, deleted]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchPages(page + 1, searchTerm, selectedBranch?.id, selectedStatus);
                setPage(prevPage => prevPage + 1);
            }
        }
    };

    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeletePageModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} page={selectedPage} />
                <ViewPageModal open={openView} handleClose={handleCloseView} pageId={selectedPage.id} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {/* Totals row */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'end'
            }}>
                <Box
                    sx={{
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        height: 60,
                        width: isTablet ? '100%' : '50%'
                    }}
                >
                    <Box width={'33%'}>
                        <Typography sx={{ mx: isTablet ? 0.4 : 1, fontSize: isTablet ? 12 : 16 }}>
                            {t('totalWallet')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalWallet)}</span>
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box width={'50%'}>
                        <Typography sx={{ mx: isTablet ? 0.4 : 1, fontSize: isTablet ? 12 : 16 }}>
                            {t('totalRemainingAmount')}: <span style={{ fontWeight: 'bold' }}>{formatPrice(totalRemainingAmount)}</span>
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: '17%'
                        }}>
                        <PdfPageList
                            data={{
                                name: searchTerm,
                                deleted: deleted,
                                branch: selectedBranch,
                                status: selectedStatus
                            }} />
                    </Box>
                </Box>
            </Box>


            {/* Filter row */}
            {
                isTablet ?
                    <Box
                        sx={{
                            mb: 2,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{ px: 2 }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            <Divider orientation="vertical" />
                            <Box width={'90%'}>
                                <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                            </Box>
                        </Box>
                        <Divider />

                        <Box sx={{
                            alignContent: 'center',
                            height: 50,
                            width: '100%'
                        }}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>
                        <Divider />

                        <Box sx={{
                            alignContent: 'center',
                            height: 50,
                            width: '100%'
                        }}>
                            <StatusDropdown
                                isFilter={true}

                                selectedStatus={selectedStatus}
                                setSelectedStatus={setSelectedStatus}
                            />
                        </Box>
                        <Divider />

                        <Box sx={{ display: 'flex', alignItems: 'center', height: 50 }}>
                            <Box sx={{
                                ml: isEn ? 3 : 0
                            }}>
                                <OnOffSwitchForDeleted
                                    checkDeleted={deleted}
                                    setCheckDeleted={setDeleted} />
                            </Box>
                            <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>
                    </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'24%'}>
                            <SearchInput placeholder={t('name')} onChange={handleSearchChange} value={searchTerm} />
                        </Box>
                        <Divider orientation="vertical" />

                        <Box width={'24%'}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'24%'}>
                            <StatusDropdown
                                isFilter={true}

                                selectedStatus={selectedStatus}
                                setSelectedStatus={setSelectedStatus}
                            />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            {/* Scrollable Table with Sticky Header */}
            <TableContainer
                component={Paper}
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{
                    boxShadow: 'none',
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    borderRadius: 2,
                    height: 'calc(100vh - 226px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="simple table">
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <TableRow>
                            <TableCell align={isEn ? 'left' : 'right'}
                                onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('id')}
                                    {sortState.sortBy === 'id' && sortIcon()}
                                </Box>
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('name')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('name')}
                                    {sortState.sortBy === 'name' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('phone')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('phone')}
                                    {sortState.sortBy === 'phone' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('branch')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('branch')}
                                    {sortState.sortBy === 'branch' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('wallet')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('wallet')}
                                    {sortState.sortBy === 'wallet' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('remainAmount')}
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('status')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('status')}
                                    {sortState.sortBy === 'status' && sortIcon()}
                                </Box>
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pages.map((page, index) => (
                            <TableRow
                                key={page.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{page.id}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{page.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{page.phone}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{page.branch && page.branch.name}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(page.wallet)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{formatPrice(page.remainAmount)}</TableCell>
                                {/* status */}
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box
                                        sx={{
                                            bgcolor: alpha(
                                                page.status === 'active' ? theme.palette.green.box : theme.palette.red.box, 0.2),
                                            width: 100,
                                            justifyContent: 'center',
                                            display: 'flex',
                                            py: 0.8,
                                            borderRadius: 0.8,
                                        }}
                                    >
                                        <Box sx={{
                                            fontWeight: 'bold',
                                            color: page.status === 'active' ? theme.palette.green.box : theme.palette.red.box,
                                        }}>
                                            {t(page.status)}</Box>
                                    </Box>
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        border: 1,
                                        borderColor: theme.palette.customGrey.borderInput,
                                        backgroundColor: theme.palette.secondary.main,
                                        borderRadius: 1,
                                    }}>
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer'
                                        }} onClick={() => handleOpenView(page)}>
                                            <RemoveRedEyeOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        {page.deletedAt === null && <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: page.isSatble ? 'not-allowed' : 'pointer',
                                            pointerEvents: page.isSatble ? 'none' : 'auto',
                                            opacity: page.isSatble ? 0.5 : 1,
                                        }}
                                            component={Link}
                                            to={`/pages/${page.id}`}
                                        >
                                            <DriveFileRenameOutlineOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>}
                                        {page.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: page.isSatble ? 'not-allowed' : 'pointer',
                                            pointerEvents: page.isSatble ? 'none' : 'auto',
                                            opacity: page.isSatble ? 0.5 : 1,
                                        }} onClick={!page.isSatble ? () => handleOpenDelete(page) : undefined}>
                                            {page.deletedAt === null ?
                                                <DeleteOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.red.bg }} />
                                                :
                                                <ReplayOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.green.bg }} />
                                            }
                                        </Box>

                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Pages;
